import LocalizedLink from '@components/Localization/LocalizedLink'
import { useNotifications } from '@components/NotificationsProvider'
import { ScrollToFormError } from '@components/ScrollToFormError'
import { schema } from '@components/_rcpo/ContactForm/schema'
import { Button, Field, Link, RolexContainer } from '@components/old-rolex-ui'
import {
  ContactFormVariant,
  Gender,
  LeadType,
  SubmitContactFormMutationVariables,
  useSubmitContactFormMutation,
} from '@generated'
import { capitalizeWords } from '@lib/capitalize-words'
import { cn } from '@lib/cn'
import { countryCodes } from '@lib/countryCodes'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { useRolexEventTracking } from '@lib/hooks/use-rolex-track-events'
import { Form, Formik } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import { Heading } from './ui/Heading'
import { Text } from './ui/Text'

const personalFields = {
  gender: '',
  firstName: '',
  lastName: '',
  email: '',
  countryCode: '31',
  phone: '',
}

const locationFields = {
  location: '',
  store: '',
}

const messageCheckboxFields = {
  message: '',
  agree: false,
  subscribe: false,
}

type Props = {
  showStores?: boolean
  subject?: string
  store?: string
  productName?: string
  location?: string
  className?: string
}

const ContactForm: FC<Props> = ({
  showStores = false,
  subject,
  store,
  location,
  productName,
  className,
}) => {
  const notifications = useNotifications()
  const [{ fetching }, submitContactForm] = useSubmitContactFormMutation()
  const { t } = useTranslation(['rolex', 'navigation', 'notifications'])
  const { track } = useRolexEventTracking()

  return (
    <div className={cn('py-[10vh]', className)}>
      <RolexContainer id="contact">
        <div className="mx-auto w-[90vw] max-w-[650px]">
          <Heading variant="medium" className="mb-2" as="h3">
            {t('detail.contactUs', { ns: 'rolex' })}
          </Heading>
          <Text variant="regular" className="leading-1 mb-5">
            {t('contact.contactFormBody', { ns: 'rolex' })}
          </Text>
          <Formik
            validationSchema={schema}
            validateOnBlur
            validateOnChange
            initialValues={{
              ...personalFields,
              ...(showStores && locationFields),
              store,
              location,
              ...messageCheckboxFields,
            }}
            onSubmit={async (values, { resetForm }) => {
              const {
                gender,
                firstName,
                lastName,
                email,
                phone,
                message,
                subscribe,
                store,
                location,
              } = values

              let variables: SubmitContactFormMutationVariables = {
                input: {
                  type: LeadType.Question,
                  gender: gender as Gender,
                  firstName: capitalizeWords(firstName),
                  lastName: capitalizeWords(lastName),
                  email,
                  phone,
                  subject,
                  message,
                  store,
                  location,
                  productName,
                  referrer: window.location.href,
                  referrerTitle: document.title,
                  variant: ContactFormVariant['Rolex'],
                },
                newsletterSignup: subscribe,
              }
              const { data, error } = await submitContactForm(variables)
              if (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error(error)
                }
                notifications.addNotification({
                  variant: 'error',
                  label: t('refreshAndTryAgain', { ns: 'notifications' }),
                })
              }

              if (data) {
                notifications.addNotification({
                  variant: 'confirmation',
                  description: t('requestSent', { ns: 'notifications' }),
                  label: t('weWillContactYou', { ns: 'notifications' }),
                })
                resetForm()
                track('contactForm')
                genericPushToTrackingLayer('ContactForm', 'Send')
              }
            }}
          >
            {({ resetForm, values }) => (
              <Form>
                <ScrollToFormError />
                <div className="grid grid-cols-[1fr_3fr] gap-x-6 sm:grid-cols-[1fr_2fr_50%]">
                  <Field
                    name="gender"
                    label={t('contactForm.salutation', { ns: 'rolex' })}
                    field="select"
                    placeholder=" "
                  >
                    <option value="">{t('contactForm.salutation', { ns: 'rolex' })}</option>
                    <option value={Gender.Male}>{t('contactForm.mr', { ns: 'rolex' })}</option>
                    <option value={Gender.Female}>{t('contactForm.mrs', { ns: 'rolex' })}</option>
                  </Field>
                  <Field
                    name="firstName"
                    label={t('contactForm.firstName', { ns: 'rolex' })}
                    field="input"
                    placeholder=" "
                  />
                  <Field
                    name="lastName"
                    gridColumn={['1 / span 2', 'auto']}
                    label={t('contactForm.lastName', { ns: 'rolex' })}
                    field="input"
                    placeholder=" "
                  />
                </div>
                <div className="grid grid-cols-[1fr_3fr] gap-x-6 sm:grid-cols-[3fr_1fr_2fr]">
                  <Field
                    name="email"
                    gridColumn={['1 / span 2', 'auto']}
                    label={t('contactForm.email', { ns: 'rolex' })}
                    field="input"
                    placeholder=" "
                  />
                  <Field
                    name="countryCode"
                    label={t('contactForm.countryCode', { ns: 'rolex' })}
                    field="select"
                  >
                    {countryCodes.map((code, index) => (
                      <option value={code.value} key={index} data-countrycode={code.countrycode}>
                        {code.text}
                      </option>
                    ))}
                  </Field>
                  <Field
                    name="phone"
                    label={t('contactForm.phone', { ns: 'rolex' })}
                    field="input"
                    placeholder=" "
                  />
                </div>
                {showStores && (
                  <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                    <Field
                      name="location"
                      label={t('contactForm.location', { ns: 'rolex' })}
                      field="select"
                      placeholder=" "
                    >
                      <option value="">{t('contactForm.location', { ns: 'rolex' })}</option>
                      <option value="amsterdam">Amsterdam</option>
                      <option value="singapore">Singapore</option>
                    </Field>
                    <Field
                      disabled={values.location === ''}
                      name="store"
                      label={t('contactForm.store', { ns: 'rolex' })}
                      field="select"
                      placeholder=" "
                    >
                      {values.location === 'singapore' ? (
                        <>
                          <option>{t('contactForm.store', { ns: 'rolex' })}</option>
                          <option>GASSAN Singapore Changi Airport | Terminal 1</option>
                          <option>GASSAN Singapore Changi Airport | Terminal 4</option>
                          <option>
                            GASSAN Rolex Boutique Singapore Changi Airport | Terminal 3
                          </option>
                        </>
                      ) : (
                        <>
                          <option>{t('contactForm.store', { ns: 'rolex' })}</option>
                          <option>Rolex Boutique HOUSE of GASSAN</option>
                          <option>Rolex Corner GASSAN Dam Square </option>
                          <option>Rolex Corner GASSAN Rotterdam</option>
                          <option>Rolex Boutique GASSAN Schiphol</option>
                        </>
                      )}
                    </Field>
                  </div>
                )}
                <div>
                  <Field
                    name="message"
                    field="textarea"
                    label={t('contactForm.message', { ns: 'rolex' })}
                    placeholder={t('contactForm.message', { ns: 'rolex' })}
                  />
                  <Field
                    name="agree"
                    label={
                      <Text variant="regular" as="span">
                        *{t('contactForm.privacyNotice', { ns: 'rolex' })}
                        <LocalizedLink href={t('privacyPolicy', { ns: 'navigation' })}>
                          <a
                            style={{ fontWeight: 'bold' }}
                            href={t('privacyPolicy', { ns: 'navigation' })}
                          >
                            {t('contactForm.privacyPolicy', { ns: 'rolex' })}
                          </a>
                        </LocalizedLink>
                        {t('contactForm.privacyNoticeAndOur', { ns: 'rolex' })}
                        <LocalizedLink href={t('cookieStatement', { ns: 'navigation' })}>
                          <a
                            style={{ fontWeight: 'bold' }}
                            href={t('cookieStatement', { ns: 'navigation' })}
                          >
                            {t('contactForm.cookieStatement', { ns: 'rolex' })}
                          </a>
                        </LocalizedLink>
                      </Text>
                    }
                    field="checkbox"
                  />
                  <Field
                    name="subscribe"
                    label={t('contactForm.subscribe', { ns: 'rolex' })}
                    field="checkbox"
                  />
                </div>
                <div className="mt-8 flex flex-row-reverse">
                  <Button type="submit" disabled={fetching}>
                    {fetching
                      ? t('contactForm.sending', { ns: 'rolex' })
                      : t('contactForm.sendMessage', { ns: 'rolex' })}
                  </Button>
                  <Link variant="tertiary" px="4" onClick={() => resetForm()}>
                    {t('contactForm.cancel', { ns: 'rolex' })}
                  </Link>
                </div>
                <Text variant="regular" className="pt-8 text-right sm:pt-10">
                  {t('contactForm.required', { ns: 'rolex' })}
                </Text>
              </Form>
            )}
          </Formik>
        </div>
      </RolexContainer>
    </div>
  )
}

export default ContactForm
